import { useEffect } from 'react';
import { getSupplierTheme } from '@/utils/supplierCustomizationCache';
import {
  guessIconMimeType,
  useWhiteLabelBranding,
} from '@/components/WhiteLabelBrandingProvider';

interface UseSupplierFaviconOptions {
  /** URL da logo do fornecedor (sobrepõe o cache de customização) */
  logoUrl?: string | null;
  /** Nome do fornecedor usado no <title> da aba */
  supplierName?: string | null;
  /** Sufixo opcional do título, ex.: "Pagamento" */
  titleSuffix?: string | null;
}

const MARKER = 'data-supplier-favicon';

/**
 * Aplica o favicon (e o título) do fornecedor nas telas da jornada do cliente
 * (checkout, confirmação, contrato, infos-festa, pagamentos avulsos).
 *
 * Fontes, em ordem de prioridade: parâmetro → cache de customização →
 * identidade resolvida pelo host white-label.
 */
export function useSupplierFavicon(options: UseSupplierFaviconOptions = {}) {
  const { logoUrl, supplierName, titleSuffix } = options;
  const branding = useWhiteLabelBranding();

  useEffect(() => {
    if (typeof document === 'undefined') return;

    let theme: ReturnType<typeof getSupplierTheme> | null = null;
    try {
      theme = getSupplierTheme();
    } catch {
      theme = null;
    }

    const icon = logoUrl || theme?.logoUrl || branding.logoUrl;
    const name = supplierName || theme?.supplierName || branding.supplierName;

    if (!icon && !name) return;

    // Guarda os ícones existentes para restaurar depois.
    const previousIcons = icon
      ? Array.from(
          document.head.querySelectorAll<HTMLLinkElement>(
            'link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]',
          ),
        ).filter((el) => !el.hasAttribute(MARKER))
      : [];

    previousIcons.forEach((el) => el.parentElement?.removeChild(el));

    const created: HTMLLinkElement[] = [];
    if (icon) {
      const type = guessIconMimeType(icon);
      (['icon', 'shortcut icon', 'apple-touch-icon'] as const).forEach((rel) => {
        const link = document.createElement('link');
        link.setAttribute('rel', rel);
        link.setAttribute('href', icon);
        if (type && rel !== 'apple-touch-icon') link.setAttribute('type', type);
        link.setAttribute(MARKER, 'true');
        document.head.appendChild(link);
        created.push(link);
      });
    }

    const previousTitle = document.title;
    if (name) {
      document.title = titleSuffix ? `${name} — ${titleSuffix}` : name;
    }

    return () => {
      created.forEach((el) => el.parentElement?.removeChild(el));
      previousIcons.forEach((el) => document.head.appendChild(el));
      if (name) document.title = previousTitle;
    };
  }, [logoUrl, supplierName, titleSuffix, branding.logoUrl, branding.supplierName]);
}

export default useSupplierFavicon;
